import React, { useEffect, useMemo, useState } from 'react';

const EMPTY_FORM = {
  code: '',
  namespace: '',
  login: '',
  password: ''
};

export default function FarmAdmin({ api }) {
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingCode, setEditingCode] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  const editing = useMemo(() => !!editingCode, [editingCode]);

  const loadFarms = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.listAdminFarms();
      setFarms(res.farms || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFarms();
  }, []);

  const onChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const startCreate = () => {
    setEditingCode('');
    setForm(EMPTY_FORM);
    setError('');
  };

  const startEdit = (farm) => {
    setEditingCode(farm.code);
    setForm({
      code: farm.code,
      namespace: farm.namespace || '',
      login: farm.login || '',
      password: ''
    });
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (editing) {
        await api.updateFarm(editingCode, {
          namespace: form.namespace,
          login: form.login,
          password: form.password
        });
      } else {
        await api.createFarm({
          code: form.code,
          namespace: form.namespace,
          login: form.login,
          password: form.password
        });
      }

      setForm(EMPTY_FORM);
      setEditingCode('');
      await loadFarms();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const disableFarm = async (code) => {
    if (!window.confirm(`Disable farm ${code}?`)) return;
    setError('');
    try {
      await api.disableFarm(code);
      await loadFarms();
    } catch (err) {
      setError(err.message);
    }
  };

  const enableFarm = async (code) => {
    setError('');
    try {
      await api.enableFarm(code);
      await loadFarms();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteFarm = async (code) => {
    if (!window.confirm(`Permanently delete farm ${code}? This cannot be undone.`)) return;
    setError('');
    try {
      await api.deleteFarmPermanent(code);
      await loadFarms();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="farm-admin">
      <div className="dashboard-header">
        <h2>Farm Configuration</h2>
        <div>
          <button className="link-btn admin-refresh" onClick={loadFarms} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="primary" onClick={startCreate} style={{ marginLeft: 8 }}>
            New Farm
          </button>
        </div>
      </div>

      {error && <div className="error card">{error}</div>}

      <form className="card admin-form" onSubmit={submit}>
        <h3>{editing ? `Edit ${editingCode}` : 'Create Farm'}</h3>
        <div className="admin-grid">
          <label>
            Farm code
            <input
              value={form.code}
              onChange={(e) => onChange('code', e.target.value.toUpperCase())}
              disabled={editing}
              placeholder="CA16"
              required
            />
          </label>
          <label>
            Namespace
            <input
              value={form.namespace}
              onChange={(e) => onChange('namespace', e.target.value)}
              required
            />
          </label>
          <label>
            Login
            <input
              value={form.login}
              onChange={(e) => onChange('login', e.target.value)}
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={form.password}
              onChange={(e) => onChange('password', e.target.value)}
              placeholder={editing ? 'Required for updates' : ''}
              required
            />
          </label>
        </div>
        <div className="admin-actions">
          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update Farm' : 'Create Farm'}
          </button>
          {editing && (
            <button type="button" onClick={startCreate}>
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="card history">
        <h3>Configured Farms</h3>
        {!farms.length && !loading ? <p className="muted">No farm configs found.</p> : null}
        {!!farms.length && (
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Host</th>
                <th>Namespace</th>
                <th>Enabled</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {farms.map((farm) => (
                <tr key={farm.code}>
                  <td>{farm.code}</td>
                  <td>{farm.host}</td>
                  <td>{farm.namespace}</td>
                  <td>{farm.enabled ? 'yes' : 'no'}</td>
                  <td>{farm.updatedAt ? new Date(farm.updatedAt).toLocaleString() : '—'}</td>
                  <td>
                    <button onClick={() => startEdit(farm)}>Edit</button>
                    {farm.enabled ? (
                      <button onClick={() => disableFarm(farm.code)} style={{ marginLeft: 6 }}>
                        Disable
                      </button>
                    ) : (
                      <button onClick={() => enableFarm(farm.code)} style={{ marginLeft: 6 }}>
                        Enable
                      </button>
                    )}
                    <button
                      onClick={() => deleteFarm(farm.code)}
                      style={{ marginLeft: 6, color: '#c00' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}